/// <reference types="vite/client" />

import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import {
  ArrowRight,
  Bot,
  CalendarClock,
  Check,
  CheckCircle2,
  CircleHelp,
  ClipboardList,
  LayoutDashboard,
  MessageSquareText,
  Plug,
  Settings2,
  Sparkles
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import "./styles.css";

type IconCard = {
  title: string;
  body: string;
  Icon: LucideIcon;
};

type TextCard = {
  title: string;
  body: string;
};

type Plan = {
  name: string;
  price: string;
  description: string;
  includes: string[];
  cta: string;
  featured?: boolean;
};

type Faq = {
  question: string;
  answer: string;
};

type SignupResponse = {
  ok?: boolean;
  error?: string;
};

const marketingApiUrl = (import.meta.env.VITE_MARKETING_API_URL || "").replace(/\/$/, "");

const passwordMeetsPolicy = (value: string) => value.length >= 6;

const navItems = [
  ["Come funziona", "#come-funziona"],
  ["Funzionalita", "#funzionalita"],
  ["Per chi e", "#per-chi"],
  ["Prezzi", "#prezzi"],
  ["FAQ", "#faq"]
];

const problemCards: IconCard[] = [
  {
    title: "Richieste perse",
    body: "Se non rispondi subito, molti clienti passano oltre. Soprattutto quando chiedono disponibilita, preventivi o appuntamenti.",
    Icon: MessageSquareText
  },
  {
    title: "Domande ripetitive",
    body: "Nome, telefono, giorno preferito, tipo di richiesta, dettagli e note: il tuo team chiede spesso le stesse cose.",
    Icon: CircleHelp
  },
  {
    title: "Dati disordinati",
    body: "Le informazioni arrivano in formati diversi e spesso incomplete. Prima di lavorare devi sistemare tutto a mano.",
    Icon: ClipboardList
  }
];

const solutionBullets = [
  "Collegamento da sito, WhatsApp, Telegram o SMS",
  "Slot configurabili",
  "Campi e metadati personalizzati",
  "Dashboard per richieste e slot",
  "Passaggio al team quando serve",
  "Predisposizione per nuovi canali e integrazioni"
];

const steps: TextCard[] = [
  {
    title: "1. Configuri il tuo flusso",
    body: "Scegli quali informazioni raccogliere: nome, telefono, tipo di richiesta, giorno preferito, note, allegati o campi utili."
  },
  {
    title: "2. Installi il widget sul sito",
    body: "Aggiungi Tevla con uno snippet. Il cliente apre la chat, fa una richiesta e riceve una risposta chiara."
  },
  {
    title: "3. Tevla raccoglie i dati giusti",
    body: "Il cliente lascia le informazioni utili senza form complicati: contatti, dettagli, preferenze, note o allegati."
  },
  {
    title: "4. Viene creata una prenotazione",
    body: "Ogni richiesta diventa una prenotazione con data, orario e metadati personalizzati, visibile dalla dashboard."
  },
  {
    title: "5. Gestisci o ricontatti",
    body: "Usa lo slot come appuntamento, preventivo, sopralluogo, promemoria operativo o lead qualificato."
  }
];

const features: IconCard[] = [
  {
    title: "Widget per il tuo sito",
    body: "Permetti ai visitatori di iniziare una richiesta in modo semplice, senza telefonare o compilare form complicati.",
    Icon: MessageSquareText
  },
  {
    title: "Raccolta intelligente",
    body: "Tevla chiede i dettagli utili, raccoglie le informazioni mancanti e mantiene chiaro il dialogo con il cliente.",
    Icon: Bot
  },
  {
    title: "Slot flessibili",
    body: "Uno slot puo essere un appuntamento, un sopralluogo, un preventivo, una consulenza o una richiesta da gestire.",
    Icon: CalendarClock
  },
  {
    title: "Metadati personalizzati",
    body: "Configura campi come urgenza, note, modello veicolo, numero persone, preferenza oraria o documenti richiesti.",
    Icon: Settings2
  },
  {
    title: "Dashboard operativa",
    body: "Visualizza richieste, conversazioni e slot in un unico posto, con informazioni gia raccolte.",
    Icon: LayoutDashboard
  },
  {
    title: "Canali pronti a evolvere",
    body: "Parti dal widget web e aggiungi nel tempo WhatsApp, Telegram, SMS o integrazioni personalizzate.",
    Icon: Plug
  },
  {
    title: "Configurazione per cliente",
    body: "Ogni attivita puo avere regole, campi, testi e flussi diversi. Tevla si adatta al tuo modo di lavorare.",
    Icon: Sparkles
  },
  {
    title: "Meno lavoro manuale",
    body: "Riduci il tempo passato a chiedere dati ripetitivi, ricopiare informazioni e ricostruire conversazioni incomplete.",
    Icon: CheckCircle2
  }
];

const audiences: TextCard[] = [
  {
    title: "Carrozzerie e officine",
    body: "Tipo di danno, modello auto, urgenza, preferenza oraria, foto e note assicurative."
  },
  {
    title: "Studi professionali",
    body: "Motivo della richiesta, documenti necessari, disponibilita, recapiti e note preliminari."
  },
  {
    title: "Ristoranti e locali",
    body: "Numero persone, fascia oraria, preferenze, allergie, evento speciale e note del cliente."
  },
  {
    title: "Servizi a domicilio",
    body: "Indirizzo, tipo di intervento, urgenza, descrizione del problema e slot preferito."
  },
  {
    title: "Professionisti su appuntamento",
    body: "Riduci messaggi ripetitivi e raccogli informazioni utili prima dell'appuntamento."
  },
  {
    title: "Team interni",
    body: "Usa Tevla come punto di ingresso ordinato per richieste, slot operativi, task e follow-up."
  }
];

const plans: Plan[] = [
  {
    name: "Early Access",
    price: "Su richiesta",
    description: "Per attivita che vogliono provare Tevla sul proprio sito e raccogliere richieste in modo piu ordinato.",
    includes: [
      "Widget web",
      "Dashboard base",
      "Slot configurabili",
      "Campi personalizzati",
      "Raccolta dati personalizzata",
      "Supporto alla configurazione iniziale"
    ],
    cta: "Iscriviti",
    featured: true
  },
  {
    name: "Pro",
    price: "In arrivo",
    description: "Per attivita che vogliono aggiungere automazioni, canali e integrazioni al proprio flusso di lavoro.",
    includes: [
      "Tutto il piano Early Access",
      "Canali aggiuntivi",
      "Integrazioni personalizzate",
      "Template per settore",
      "Report e automazioni"
    ],
    cta: "Parla con noi"
  }
];

const faqs: Faq[] = [
  {
    question: "Tevla e un calendario per prenotazioni?",
    answer:
      "Non solo. Tevla crea prenotazioni organizzate e raccoglie le informazioni importanti prima che arrivino al tuo team. Ogni prenotazione puo avere metadati personalizzati."
  },
  {
    question: "Devo cambiare gestionale?",
    answer:
      "No. La prima versione puo essere usata come punto di raccolta e organizzazione delle richieste. Le integrazioni possono essere aggiunte in base alle esigenze."
  },
  {
    question: "Posso scegliere quali dati chiedere al cliente?",
    answer:
      "Si. Puoi configurare campi e metadati da raccogliere per ogni flusso: contatti, note, dettagli tecnici o informazioni specifiche del tuo settore."
  },
  {
    question: "WhatsApp e Telegram sono gia disponibili?",
    answer:
      "La piattaforma e predisposta per supportare piu canali. Nella prima fase il canale principale e il widget web. WhatsApp, Telegram e SMS possono essere valutati come integrazioni successive."
  },
  {
    question: "Serve installare qualcosa?",
    answer:
      "No. Il widget viene inserito nel sito tramite uno snippet. La configurazione avviene da pannello web."
  },
  {
    question: "Posso provarlo sulla mia attivita?",
    answer:
      "Si. Tevla e in accesso anticipato: puoi iscriverti e configurare un primo flusso per la tua attivita."
  }
];

const getPath = () => window.location.pathname.replace(/\/$/, "") || "/";

const Logo = () => (
  <a className="brand" href="/" aria-label="Tevla home">
    <span className="brand-mark" aria-hidden="true">
      <i />
    </span>
    <span>
      <strong>Tevla</strong>
      <small>Collegamento</small>
    </span>
  </a>
);

const Header = () => (
  <header className="site-header">
    <Logo />
    <nav className="site-nav" aria-label="Navigazione principale">
      {navItems.map(([label, href]) => (
        <a href={href} key={href}>
          {label}
        </a>
      ))}
    </nav>
    <div className="header-actions">
      <a className="button button-ghost" href="https://app.tevla.eu">
        Accedi
      </a>
      <a className="button button-primary" href="/iscriviti">
        <span>Iscriviti</span>
        <ArrowRight size={17} strokeWidth={2.2} />
      </a>
    </div>
  </header>
);

const Footer = () => (
  <footer className="site-footer">
    <div className="footer-brand">
      <Logo />
      <p>Collegamento per trasformare richieste, messaggi e contatti in prenotazioni organizzate con dati personalizzati.</p>
      <small>&copy; 2026 Tevla. Tutti i diritti riservati.</small>
    </div>
    <div className="footer-columns">
      <FooterColumn title="Prodotto" links={navItems} />
      <FooterColumn
        title="Account"
        links={[
          ["Iscriviti", "/iscriviti"],
          ["Accedi", "https://app.tevla.eu"],
          ["Contatti", "/contatti"]
        ]}
      />
      <FooterColumn
        title="Legale"
        links={[
          ["Privacy", "/privacy"],
          ["Cookie", "/cookie"],
          ["Termini", "/termini"]
        ]}
      />
    </div>
  </footer>
);

const FooterColumn = ({ title, links }: { title: string; links: string[][] }) => (
  <nav aria-label={title}>
    <strong>{title}</strong>
    {links.map(([label, href]) => (
      <a href={href} key={`${title}-${href}`}>
        {label}
      </a>
    ))}
  </nav>
);

const WidgetPreview = () => (
  <div className="widget-preview" aria-label="Anteprima assistente Tevla">
    <div className="widget-preview__header">
      <span className="widget-avatar" aria-hidden="true">
        <Bot size={20} />
      </span>
      <div>
        <strong>Assistente Tevla</strong>
        <small>Widget web disponibile</small>
      </div>
    </div>
    <div className="widget-thread">
      <ChatBubble speaker="Cliente" text="Ciao, vorrei prenotare un sopralluogo per domani." />
      <ChatBubble tone="bot" speaker="Tevla" text="Certo. Ti chiedo due informazioni cosi preparo la richiesta nel modo corretto." />
      <ChatBubble tone="bot" speaker="Tevla" text="Che tipo di intervento ti serve?" />
      <ChatBubble speaker="Cliente" text="Preventivo per un danno alla macchina." />
      <div className="slot-list" aria-label="Slot disponibili">
        {["Domani 09:30", "Domani 11:00", "Domani 15:30"].map((slot) => (
          <span key={slot}>{slot}</span>
        ))}
      </div>
      <ChatBubble speaker="Cliente" text="11:00" compact />
      <ChatBubble tone="bot" speaker="Tevla" text="Ottimo. Ho salvato lo slot con le informazioni raccolte." />
    </div>
    <div className="widget-badges" aria-label="Stato richiesta">
      {["Richiesta raccolta", "Slot creato", "Dati organizzati"].map((label) => (
        <span key={label}>
          <Check size={15} />
          {label}
        </span>
      ))}
    </div>
  </div>
);

const ChatBubble = ({
  speaker,
  text,
  tone = "customer",
  compact = false
}: {
  speaker: string;
  text: string;
  tone?: "customer" | "bot";
  compact?: boolean;
}) => (
  <article className={`chat-bubble chat-bubble--${tone}${compact ? " chat-bubble--compact" : ""}`}>
    <small>{speaker}</small>
    <p>{text}</p>
  </article>
);

const HomePage = () => (
  <main className="page page-home">
    <section className="hero-section">
      <div className="hero-copy">
        <span className="eyebrow">Collegamento per attivita e team</span>
        <h1>Trasforma ogni richiesta in una prenotazione organizzata</h1>
        <p>
          Tevla risponde ai clienti dal tuo sito, da WhatsApp, Telegram o SMS, raccoglie le informazioni importanti e crea prenotazioni con dati
          personalizzati per aiutarti a gestire appuntamenti, preventivi, sopralluoghi e richieste senza perdere tempo.
        </p>
        <div className="hero-actions">
          <a className="button button-primary" href="/iscriviti">
            <span>Iscriviti</span>
            <ArrowRight size={18} />
          </a>
          <a className="button button-ghost" href="#come-funziona">
            Vedi come funziona
          </a>
        </div>
        <p className="hero-microcopy">
          Installazione semplice. Configurabile. Pensato per attivita che ricevono richieste ogni giorno.
        </p>
      </div>
      <WidgetPreview />
    </section>

    <section className="content-section section-band section-band--warm" id="problema">
      <SectionHeading
        eyebrow="Il problema"
        title="Le richieste arrivano ovunque. Gestirle bene e difficile."
        body="Un cliente scrive dal sito. Un altro chiede disponibilita fuori orario. Altri vogliono informazioni prima di fissare un appuntamento. Il risultato: conversazioni sparse, risposte in ritardo, dati incompleti e tempo perso."
        centered
      />
      <div className="card-grid card-grid--three">
        {problemCards.map((card) => (
          <IconFeatureCard {...card} key={card.title} />
        ))}
      </div>
    </section>

    <section className="content-section solution-section">
      <div className="split-layout">
        <SectionHeading
          eyebrow="La soluzione"
          title="Un collegamento tra i tuoi clienti e il tuo team"
          body="Tevla non e solo un calendario. E un collegamento configurabile che dialoga con il cliente, capisce cosa serve, raccoglie i dati giusti e crea una prenotazione ordinata con tutti i metadati utili."
        />
        <div className="bullet-panel">
          {solutionBullets.map((item) => (
            <span key={item}>
              <CheckCircle2 size={18} />
              {item}
            </span>
          ))}
        </div>
      </div>
      <a className="button button-primary section-cta" href="/iscriviti">
        Organizza le tue richieste con Tevla
      </a>
    </section>

    <section className="content-section section-band" id="come-funziona">
      <SectionHeading
        eyebrow="Come funziona"
        title="Dal messaggio del cliente allo slot pronto da gestire"
        centered
      />
      <div className="steps-grid">
        {steps.map((step) => (
          <article className="step-card" key={step.title}>
            <h3>{step.title}</h3>
            <p>{step.body}</p>
          </article>
        ))}
      </div>
    </section>

    <section className="content-section" id="funzionalita">
      <SectionHeading
        eyebrow="Funzionalita"
        title="Tutto quello che serve per raccogliere richieste migliori"
        centered
      />
      <div className="card-grid card-grid--four">
        {features.map((feature) => (
          <IconFeatureCard {...feature} key={feature.title} />
        ))}
      </div>
    </section>

    <section className="content-section section-band section-band--blue">
      <div className="split-layout split-layout--center">
        <SectionHeading
          eyebrow="Flessibilita"
          title="Non tutte le prenotazioni sono uguali. Tevla lo sa."
          body="Una prenotazione per un ristorante non ha gli stessi dati di un sopralluogo tecnico. Decidi tu quali informazioni servono e Tevla le raccoglie nel flusso di richiesta."
        />
        <div className="metadata-panel">
          <strong>Slot sopralluogo</strong>
          <dl>
            <div>
              <dt>Tipo richiesta</dt>
              <dd>Preventivo danno auto</dd>
            </div>
            <div>
              <dt>Urgenza</dt>
              <dd>Entro 48 ore</dd>
            </div>
            <div>
              <dt>Slot scelto</dt>
              <dd>Domani, 11:00</dd>
            </div>
            <div>
              <dt>Note</dt>
              <dd>Foto da verificare prima della conferma</dd>
            </div>
          </dl>
        </div>
      </div>
    </section>

    <section className="content-section" id="per-chi">
      <SectionHeading
        eyebrow="Per chi e"
        title="Pensato per chi riceve richieste e deve trasformarle in lavoro reale"
        body="Tevla e utile per tutte le attivita che ricevono contatti online e hanno bisogno di raccogliere informazioni prima di fissare, confermare o gestire una richiesta."
        centered
      />
      <div className="card-grid card-grid--three">
        {audiences.map((audience) => (
          <TextFeatureCard {...audience} key={audience.title} />
        ))}
      </div>
    </section>

    <section className="mid-cta">
      <div>
        <h2>Inizia a ricevere richieste piu ordinate</h2>
        <p>Installa Tevla sul tuo sito e trasforma le conversazioni dei clienti in slot chiari, completi e facili da gestire.</p>
        <small>Stiamo aprendo l'accesso a un numero limitato di attivita pilota.</small>
      </div>
      <div className="mid-cta__actions">
        <a className="button button-light" href="/iscriviti">
          Iscriviti
        </a>
        <a className="button button-outline-light" href="/contatti">
          Parla con noi
        </a>
      </div>
    </section>

    <section className="content-section section-band section-band--soft" id="prezzi">
      <SectionHeading
        eyebrow="Prezzi"
        title="Prezzi semplici, pensati per partire"
        body="Tevla e in fase di accesso anticipato. Stiamo lavorando con le prime attivita per configurare flussi reali, misurare il valore e costruire le integrazioni piu utili."
        centered
      />
      <div className="pricing-grid">
        {plans.map((plan) => (
          <PricingCard {...plan} key={plan.name} />
        ))}
      </div>
      <p className="section-note">Nessuna commissione sulle richieste raccolte dal tuo sito.</p>
    </section>

    <section className="content-section" id="faq">
      <SectionHeading eyebrow="FAQ" title="Domande frequenti" centered />
      <div className="faq-grid">
        {faqs.map((faq) => (
          <details className="faq-item" key={faq.question}>
            <summary>{faq.question}</summary>
            <p>{faq.answer}</p>
          </details>
        ))}
      </div>
    </section>
  </main>
);

const SectionHeading = ({
  eyebrow,
  title,
  body,
  centered = false
}: {
  eyebrow: string;
  title: string;
  body?: string;
  centered?: boolean;
}) => (
  <div className={centered ? "section-heading section-heading--center" : "section-heading"}>
    <span className="eyebrow">{eyebrow}</span>
    <h2>{title}</h2>
    {body ? <p>{body}</p> : null}
  </div>
);

const IconFeatureCard = ({ title, body, Icon }: IconCard) => (
  <article className="feature-card">
    <span className="feature-card__icon">
      <Icon size={23} strokeWidth={2.2} />
    </span>
    <h3>{title}</h3>
    <p>{body}</p>
  </article>
);

const TextFeatureCard = ({ title, body }: TextCard) => (
  <article className="text-card">
    <h3>{title}</h3>
    <p>{body}</p>
  </article>
);

const PricingCard = ({ name, price, description, includes, cta, featured }: Plan) => (
  <article className={featured ? "pricing-card is-featured" : "pricing-card"}>
    {featured ? <span className="pricing-card__badge">Accesso anticipato</span> : null}
    <div>
      <h3>{name}</h3>
      <strong>{price}</strong>
    </div>
    <p>{description}</p>
    <ul>
      {includes.map((item) => (
        <li key={item}>
          <Check size={17} />
          {item}
        </li>
      ))}
    </ul>
    <a className="button button-primary" href={cta === "Iscriviti" ? "/iscriviti" : "/contatti"}>
      {cta}
    </a>
  </article>
);

const SignupPage = () => {
  const [step, setStep] = useState<"account" | "code" | "ready">("account");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const title = step === "account" ? "Iscriviti a Tevla" : step === "code" ? "Verifica la tua email" : "Accesso pronto";
  const description =
    step === "account"
      ? "Crea il tuo account con utente, email e password. Le informazioni operative vengono richieste dopo l'accesso, nell'onboarding."
      : step === "code"
        ? `Inserisci il codice inviato a ${email || "indirizzo email"} per accedere all'onboarding.`
        : "Account verificato. Ora puoi entrare nell'app e completare l'onboarding con informazioni attivita, slot, canali e AI.";

  const requestSignup = async () => {
    setError("");
    if (!marketingApiUrl) {
      setError("Iscrizione non configurata: manca VITE_MARKETING_API_URL.");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      setError("Inserisci un indirizzo email valido.");
      return;
    }
    if (!passwordMeetsPolicy(password)) {
      setError("La password deve avere almeno 6 caratteri.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Le password non coincidono.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${marketingApiUrl}/marketing/signup`, {
        body: JSON.stringify({ email, password, username }),
        headers: { "content-type": "application/json" },
        method: "POST"
      });
      const payload = (await response.json()) as SignupResponse;

      if (!response.ok || !payload.ok) {
        setError(signupErrorMessage(payload.error));
        return;
      }

      setStep("code");
    } catch {
      setError("Non riesco a contattare il servizio di iscrizione. Riprova tra poco.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmSignup = async () => {
    setError("");
    if (!marketingApiUrl) {
      setError("Iscrizione non configurata: manca VITE_MARKETING_API_URL.");
      return;
    }
    if (!code.trim()) {
      setError("Inserisci il codice ricevuto via email.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${marketingApiUrl}/marketing/signup/confirm`, {
        body: JSON.stringify({ code, email }),
        headers: { "content-type": "application/json" },
        method: "POST"
      });
      const payload = (await response.json()) as SignupResponse;

      if (!response.ok || !payload.ok) {
        setError(signupErrorMessage(payload.error));
        return;
      }

      setStep("ready");
    } catch {
      setError("Non riesco a verificare il codice. Riprova tra poco.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="page">
      <section className="auth-shell">
        <article className="tevla-card tevla-card--muted login-card">
          <div className="tevla-card__body">
            <div className="tevla-section-heading is-centered">
              <div className="tevla-section-heading__copy">
                <span className="tevla-badge tevla-section-heading__eyebrow">Iscriviti</span>
                <h2>{title}</h2>
                <p>{description}</p>
              </div>
            </div>
            {error ? (
              <p className="auth-alert" role="alert">
                {error}
              </p>
            ) : null}
        {step === "account" ? (
          <form className="login-form" onSubmit={(event) => event.preventDefault()}>
            <div className="tevla-field">
              <label>Utente</label>
              <input className="tevla-input" name="username" onChange={(event) => setUsername(event.target.value)} placeholder="nomeutente" value={username} />
            </div>
            <div className="tevla-field">
              <label>Email</label>
              <input
                className="tevla-input"
                name="email"
                onChange={(event) => setEmail(event.target.value)}
                placeholder="nome@azienda.it"
                type="email"
                value={email}
              />
            </div>
            <div className="tevla-field">
              <label>Password</label>
              <input
                autoComplete="new-password"
                className="tevla-input"
                name="password"
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Almeno 6 caratteri"
                type="password"
                value={password}
              />
            </div>
            <div className="tevla-field">
              <label>Conferma password</label>
              <input
                autoComplete="new-password"
                className="tevla-input"
                name="confirmPassword"
                onChange={(event) => setConfirmPassword(event.target.value)}
                placeholder="Ripeti la password"
                type="password"
                value={confirmPassword}
              />
            </div>
            <button className="tevla-button tevla-button--primary" disabled={isSubmitting} onClick={requestSignup} type="button">
              <span className="tevla-button__label">
                {isSubmitting ? "Invio in corso" : "Iscriviti"}
                <ArrowRight size={17} />
              </span>
            </button>
          </form>
        ) : null}
        {step === "code" ? (
          <form className="login-form" onSubmit={(event) => event.preventDefault()}>
            <div className="tevla-field">
              <label>Codice ricevuto via email</label>
              <input
                className="tevla-input"
                autoComplete="one-time-code"
                inputMode="numeric"
                name="code"
                onChange={(event) => setCode(event.target.value)}
                placeholder="123456"
                value={code}
              />
            </div>
            <button className="tevla-button tevla-button--primary" disabled={isSubmitting} onClick={confirmSignup} type="button">
              <span className="tevla-button__label">
                {isSubmitting ? "Verifica in corso" : "Verifica e accedi"}
                <ArrowRight size={17} />
              </span>
            </button>
            <button className="tevla-button tevla-button--secondary" disabled={isSubmitting} onClick={() => setStep("account")} type="button">
              <span className="tevla-button__label">Modifica email</span>
            </button>
          </form>
        ) : null}
        {step === "ready" ? (
          <div className="auth-ready">
            <CheckCircle2 size={24} />
            <a className="tevla-button tevla-button--primary" href="https://app.tevla.eu/onboarding">
              <span className="tevla-button__label">Accedi all'onboarding</span>
            </a>
          </div>
        ) : null}
            <div className="auth-meta">
              <p>
                Hai gia un account? <a href="https://app.tevla.eu/login">Accedi</a>
              </p>
            </div>
          </div>
        </article>
      </section>
    </main>
  );
};

const signupErrorMessage = (error?: string) => {
  switch (error) {
    case "CodeMismatchException":
      return "Il codice inserito non e corretto.";
    case "ExpiredCodeException":
      return "Il codice e scaduto. Torna indietro e richiedine uno nuovo.";
    case "InvalidParameterException":
    case "InvalidPasswordException":
      return "Cognito ha rifiutato la richiesta. Controlla email e utente.";
    case "LimitExceededException":
      return "Troppi tentativi ravvicinati. Riprova tra qualche minuto.";
    case "invalid_email":
      return "Inserisci un indirizzo email valido.";
    case "invalid_password":
      return "La password deve avere almeno 6 caratteri.";
    case "missing_code":
      return "Inserisci il codice ricevuto via email.";
    case "signup_not_configured":
      return "Iscrizione non configurata sul backend.";
    default:
      return "Non siamo riusciti a completare l'iscrizione. Riprova tra poco.";
  }
};

const LegalPage = ({ title }: { title: string }) => (
  <main className="page">
    <section className="page-hero">
      <span className="eyebrow">Tevla</span>
      <h1>{title}</h1>
      <p>Documento in preparazione per la versione commerciale.</p>
    </section>
  </main>
);

const Router = () => {
  const path = getPath();

  if (path === "/iscriviti" || path === "/contatti") return <SignupPage />;
  if (path === "/privacy") return <LegalPage title="Privacy" />;
  if (path === "/cookie") return <LegalPage title="Cookie" />;
  if (path === "/termini") return <LegalPage title="Termini" />;
  return <HomePage />;
};

const App = () => (
  <div className="site-shell">
    <Header />
    <Router />
    <Footer />
  </div>
);

createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
